import { useEffect } from 'react'

import { useUiStore, type Theme } from '@/shared/stores/ui-store'

const DARK_QUERY = '(prefers-color-scheme: dark)'

/**
 * Whether the dark palette applies, given a choice and what the system says.
 *
 * Pure, and separate from the effect below, because this is the whole of the
 * decision and the effect is only the DOM write.
 */
export function isDark(theme: Theme, systemPrefersDark: boolean): boolean {
  return theme === 'system' ? systemPrefersDark : theme === 'dark'
}

/**
 * Paints the chosen theme, and keeps following the system while that is the
 * choice.
 *
 * `.dark` on `<html>` is what the token file switches on. Applied from an effect
 * rather than at module load so it survives a store change, and re-subscribed
 * when the choice changes so the media listener is only attached while it can
 * matter.
 *
 * A first paint in the wrong palette is possible here and not worth solving with
 * a blocking inline script: the shell renders behind a session bootstrap that
 * already costs a round trip, so there is no frame of content to flash.
 */
export function useTheme(): void {
  const theme = useUiStore((state) => state.theme)

  useEffect(() => {
    const media = window.matchMedia(DARK_QUERY)
    const apply = () => {
      document.documentElement.classList.toggle('dark', isDark(theme, media.matches))
    }

    apply()
    if (theme !== 'system') return

    media.addEventListener('change', apply)
    return () => {
      media.removeEventListener('change', apply)
    }
  }, [theme])
}
